import type {
  MilestoneStatus,
  ProjectTimeline as ProjectTimelineData,
} from '../types/project';

interface ProjectTimelineProps {
  timeline: ProjectTimelineData;
  /** Headline slip, echoed into the slip band. Empty when on-track. */
  slipLabel: string;
  /** Whether the project is on-track - hides the slip band + projected flag. */
  isOnTrack: boolean;
}

/** Milestone node fill + ring by status. */
const NODE_STYLE: Record<MilestoneStatus, string> = {
  done: 'bg-emerald-500 ring-emerald-100',
  active: 'bg-[#00AEEF] ring-[#00AEEF]/20',
  'at-risk': 'bg-rose-500 ring-rose-100',
  upcoming: 'bg-white ring-zinc-100 border border-zinc-300',
};

/** Centers an absolutely-positioned marker on its point, RTL-safe. */
const CENTER_X = '-translate-x-1/2 rtl:translate-x-1/2';

/**
 * The project schedule as a horizontal timeline: milestones plotted along a
 * month axis, with the committed deadline and Intera's projected delivery
 * flagged so the slip between them reads at a glance.
 */
export function ProjectTimeline({
  timeline,
  slipLabel,
  isOnTrack,
}: ProjectTimelineProps) {
  const { axis, milestones, committedPosition, projectedPosition } = timeline;
  /** Fill the track up to the furthest done/active milestone. */
  const progressEnd = milestones.reduce(
    (furthest, milestone) =>
      milestone.status === 'done' || milestone.status === 'active'
        ? Math.max(furthest, milestone.position)
        : furthest,
    0
  );
  const slipStart = Math.min(committedPosition, projectedPosition);
  const slipWidth = Math.abs(projectedPosition - committedPosition);

  return (
    <section className="overflow-hidden rounded-3xl border border-zinc-100 bg-white p-7 shadow-[0_30px_70px_-44px_rgba(16,27,58,0.45)] sm:p-8">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-zinc-500">
          Timeline
        </h3>
        <div className="flex items-center gap-4 text-[11px] font-medium text-zinc-500">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Committed
          </span>
          {!isOnTrack && (
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-rose-500" />
              Projected
            </span>
          )}
        </div>
      </div>

      {/*
        Plot area, in stacked vertical zones so nothing overlaps:
        top (0-40px) flag pills · 44px the track + nodes · below it the staggered
        milestone labels in two rows.
      */}
      <div className="relative mx-1 mt-12 mb-2 h-44">
        {/* Committed flag. */}
        <Flag position={committedPosition} tone="committed" label="Committed" />
        {/* Projected flag + slip band. */}
        {!isOnTrack && (
          <>
            <Flag
              position={projectedPosition}
              tone="projected"
              label="Projected"
            />
            <div
              aria-hidden="true"
              className="absolute top-[44px] z-10 h-2.5 rounded-full bg-[repeating-linear-gradient(45deg,rgba(244,63,94,0.18),rgba(244,63,94,0.18)_4px,transparent_4px,transparent_8px)] ring-1 ring-inset ring-rose-200"
              style={{
                insetInlineStart: `${slipStart}%`,
                width: `${slipWidth}%`,
              }}
            />
            {slipLabel && (
              <span
                className={`absolute top-[20px] z-20 whitespace-nowrap text-[11px] font-semibold text-rose-600 ${CENTER_X}`}
                style={{ insetInlineStart: `${slipStart + slipWidth / 2}%` }}
              >
                {slipLabel}
              </span>
            )}
          </>
        )}

        {/* Track. */}
        <div className="absolute inset-x-0 top-[44px] h-2.5 -translate-y-px overflow-hidden rounded-full bg-zinc-100">
          <div
            className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-[#00AEEF]"
            style={{ width: `${progressEnd}%` }}
          />
        </div>

        {/* Milestone nodes; labels all sit below the track, staggered two rows. */}
        {milestones.map((milestone, milestoneIndex) => {
          const isFarRow = milestoneIndex % 2 === 1;
          return (
            <div
              key={milestone.id}
              className={`absolute top-[50px] z-20 ${CENTER_X}`}
              style={{ insetInlineStart: `${milestone.position}%` }}
            >
              <span
                className={`block h-3.5 w-3.5 rounded-full ring-4 ${
                  NODE_STYLE[milestone.status]
                }`}
              />
              <div
                className={`absolute w-32 text-center ${CENTER_X} ${
                  isFarRow ? 'top-14' : 'top-7'
                }`}
                style={{ insetInlineStart: '7px' }}
              >
                <span className="block text-xs font-semibold leading-tight text-zinc-700">
                  {milestone.label}
                </span>
                <span className="block text-[11px] text-zinc-400">
                  {milestone.dateLabel}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Month axis. */}
      <div className="mt-3 flex items-center justify-between border-t border-zinc-100 pt-2 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
        {axis.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </div>
    </section>
  );
}

/** A vertical flag marking the committed or projected date on the track. */
function Flag({
  position,
  tone,
  label,
}: {
  position: number;
  tone: 'committed' | 'projected';
  label: string;
}) {
  const isProjected = tone === 'projected';
  const lineColor = isProjected ? 'bg-rose-400' : 'bg-emerald-400';
  const pill = isProjected
    ? 'bg-rose-50 text-rose-700 ring-rose-200'
    : 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  return (
    <div
      className={`absolute top-0 z-20 ${CENTER_X}`}
      style={{ insetInlineStart: `${position}%` }}
    >
      <span
        className={`absolute -top-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-semibold ring-1 ring-inset ${CENTER_X} ${pill}`}
        style={{ insetInlineStart: '0' }}
      >
        {label}
      </span>
      {/* Short connector from the pill down to the track only. */}
      <span
        aria-hidden="true"
        className={`absolute top-6 h-6 w-px ${lineColor}`}
      />
    </div>
  );
}
